'use client';
import { Button, Stack, Typography } from '@mui/material';
import React, { Component, type ReactNode } from 'react';

import { Iconify } from '@infor/ui-lib/src/components/iconify';
import { AppVersion } from '@infor/ui-lib/src/components-capacitor/app-version';
import { storage } from '@infor/utils/src/storage';

import {
  getMonitoringService,
  type MonitoringServiceError,
} from '../../lib/observability/monitoring';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidMount(): void {
    getMonitoringService()?.init();
    getMonitoringService()?.addListeners();
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const errorMonitoring: MonitoringServiceError = {
      error,
      componentName: 'ErrorBoundary',
      context: info,
    };

    getMonitoringService()?.captureError(errorMonitoring);
  }

  override componentWillUnmount(): void {
    getMonitoringService()?.removeListeners();
  }

  handleReload = () => {
    storage.remove('user-store');

    storage.remove('navigation-history-app-inforcfc');

    window.location.href = '/';
  };

  override render() {
    if (this.state.hasError) {
      return (
        <Stack
          alignItems={'center'}
          justifyContent={'center'}
          height={'100vh'}
          gap={2}
        >
          <Iconify
            sx={{ color: 'error.light', width: 60, height: 60 }}
            icon={'material-symbols-light:error-outline'}
          />
          <Typography
            sx={{
              textAlign: 'center',
              color: 'error.light',
              fontWeight: '500',
            }}
          >
            Ops! Algo deu errado. Tente reiniciar o aplicativo.
          </Typography>

          <Button
            variant="contained"
            color="error"
            size="large"
            sx={{ mt: 2 }}
            onClick={this.handleReload}
          >
            Reiniciar
          </Button>
          <AppVersion sx={{ color: 'text.primary' }} />
        </Stack>
      );
    }

    return <>{this.props.children}</>;
  }
}
